import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Layers, Loader2 } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { ConfirmModal } from '@/components/ConfirmModal';

import type { AssetResponse, CascadeAsset, SkippedAsset } from '../../api';
import {
  deleteAssets,
  downloadAsset,
  downloadSelection,
  MAX_DOWNLOAD_SELECTION,
  SKIP_CASCADE_UNCONFIRMED,
} from '../../api';
import { isDownloadable } from '../../constants/taxonomy';
import { useFileDownload } from '../../useFileDownload';
import { HintTooltip } from '../HintTooltip';
import { useAssetPublish } from './useAssetPublish';
import { ASSET_URLS_QUERY_KEY } from './useAssetUrls';
import type { AssetSelection } from './useAssetSelection';

/**
 * Selection summary, the select-all / clear controls, Open, Delete and Publish.
 *
 * Always rendered, even at zero selected, so the grid doesn't reflow the
 * moment the first card is clicked. At zero it carries the hint for the
 * gestures, which are otherwise undiscoverable.
 *
 * Publish lives here rather than in its own bar because this is already the
 * selection-actions strip: a second bar would compete with it for the same
 * space and say the same "N selected" twice. Delete joins it for the same
 * reason, and because both act on exactly this selection.
 *
 * Open/Edit is load-bearing rather than a convenience. There is no metadata
 * column any more — editing happens in the overlay — so this is the only
 * control that reaches it for a selection, and for a single asset it is the
 * only one a keyboard user can find at all: the alternatives are double-click
 * and a button that appears on hover.
 */

interface SelectionToolbarProps {
  selection: AssetSelection;
  totalVisible: number;
  /**
   * The selected rows, not just their ids: the button needs to count how many
   * lack a nameplate before the click, and that only exists on the asset.
   */
  selectedAssets: AssetResponse[];
  /**
   * False on the Published tab, where there is nothing left to publish, and
   * for roles without `can_manage_assets`.
   */
  canPublish: boolean;
  /** `can_delete_assets`. Without it there is no Delete button at all. */
  canDelete: boolean;
  /**
   * `can_download_assets`. Without it there is no Download button at all:
   * hidden rather than greyed, because a client never takes files out of the
   * library and a disabled button would only make them wonder what they miss.
   */
  canDownload?: boolean;
  /**
   * Whether the overlay will let this selection be edited. Only changes the
   * label: a read-only overlay reached through "Edit" promises something it
   * then refuses.
   */
  canEdit: boolean;
  /** Open the selection in the detail overlay, which is where metadata lives. */
  onEdit: () => void;
  /** Whether the Published tab is active (shows briefing-row hints). */
  showBriefingHints: boolean;
  /** Whether a briefing row is currently selected (Published tab). */
  hasRowSelected?: boolean;
  /**
   * A row is picked, so the selection is for the basket: Download, Delete and
   * bulk Edit are shown but disabled, saying how to get them back. Shown rather than
   * hidden, because a role that downloads every day would otherwise conclude
   * the button had gone.
   */
  isBriefingRow?: boolean;
  /**
   * Whether nothing can be selected until a row is picked: true for a role
   * whose selection only fills the basket. Without it, the empty toolbar shows
   * the usual click hint even before a row is picked.
   */
  needsRowToSelect?: boolean;
  /**
   * Whether this role can select at all. False for a role with nothing to do
   * with a selection (a viewer), whose cards open on a single click instead —
   * so the hint says that, not how to select.
   */
  canSelect?: boolean;
  /** Number of published assets matching the selected row. */
  eligibleCount?: number;
  /**
   * Add the eligible part of the selection to the basket. Absent unless a row
   * is selected — the basket is filled per row, so without one there is nothing
   * to add against.
   */
  onAddSelectionToBasket?: () => void;
  /** How many of the selected assets are eligible and not already basketed. */
  addableCount?: number;
  /** True when matching assets exist for the selected row but all have expired rights. */
  hasExpiredMatches?: boolean;
  /** True when the selected row has no tier: bad media-plan data that no asset can match. */
  rowMissingTier?: boolean;
  /** Whether the hierarchical grouping view is active. */
  isGrouped?: boolean;
  /** Toggle callback for grouping view. */
  onToggleGrouping?: () => void;
}

/**
 * The red toast after a delete that left something behind, in words a user
 * would say: which file, and why, one line each. The backend's reasons are
 * written to follow the file name ("Couldn't delete hero.png. It's used in an
 * active brief.").
 *
 * Capped at three lines: a 200-asset selection that hits one rule 200 times
 * should not fill the screen.
 */
export function describeDeleteSkips(
  deleted: number,
  skipped: SkippedAsset[],
  names: Record<string, string>,
): string {
  const lines: string[] = [];
  if (deleted > 0) lines.push(`Deleted ${deleted} asset${deleted === 1 ? '' : 's'}.`);
  for (const skip of skipped.slice(0, 3)) {
    const name = names[skip.asset_id];
    lines.push(`Couldn't delete ${name ?? 'an asset'}. ${skip.reason}`);
  }
  if (skipped.length > 3) {
    lines.push(`${skipped.length - 3} more couldn't be deleted.`);
  }
  return lines.join('\n');
}

/**
 * The badge for one asset in the cascade list. An upload still in flight is
 * deleted too, and is labelled as such rather than passed off as staged.
 */
function cascadeBadge(status: string): { label: string; className: string } {
  if (status === 'published')
    return { label: 'Published', className: 'bg-emerald-50 text-emerald-700' };
  if (status === 'uploading')
    return { label: 'Uploading', className: 'bg-slate-100 text-slate-600' };
  return { label: 'Staged', className: 'bg-amber-50 text-amber-700' };
}

/**
 * The second confirmation's sentence. Counted from the backend's own list,
 * which covers derivatives that are not on this page.
 */
function describeCascade(assets: CascadeAsset[], sources: number): string {
  const source = sources === 1 ? 'the source image' : 'the source images';
  const what =
    assets.length === 1 ? '1 asset was made from' : `${assets.length} assets were made from`;
  return `${what} ${source} you're deleting, and will be deleted with ${
    sources === 1 ? 'it' : 'them'
  }. This can't be undone.`;
}

export function SelectionToolbar({
  selection,
  totalVisible,
  selectedAssets,
  canPublish,
  canDelete,
  canDownload = false,
  canEdit,
  onEdit,
  showBriefingHints,
  hasRowSelected = false,
  isBriefingRow = false,
  needsRowToSelect = false,
  canSelect = true,
  eligibleCount = 0,
  onAddSelectionToBasket,
  addableCount = 0,
  hasExpiredMatches = false,
  rowMissingTier = false,
  isGrouped = false,
  onToggleGrouping,
}: SelectionToolbarProps) {
  const { count, selectAll, clear } = selection;
  const allSelected = totalVisible > 0 && count === totalVisible;
  const queryClient = useQueryClient();

  // Delete is guarded by a confirm rather than an undo: the backend hard-deletes
  // the row and the object, so there is nothing left to undo it with.
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  /**
   * The cascade the backend refused to do without being asked, once it has
   * told us how big it is.
   *
   * Two confirms rather than one, and the second only appears when there is
   * actually something extra to lose: deleting a source image takes the assets
   * cut from it, and that is a different, larger decision than the one the
   * first modal described. Warning about it up front on every delete would
   * make the common case — a few stray uploads with nothing hanging off them —
   * read as though it were dangerous.
   *
   * Holds the backend's own reason strings rather than a count we recompute:
   * the server knows which derivatives exist, the grid only knows what is on
   * this page.
   *
   * Carries the skipped ids alongside them because the retry must send *those*
   * ids and not the current selection. The first pass has already deleted
   * everything else in the selection and invalidated the list, but
   * `selectedAssets` is derived from the fetched rows, so until that refetch
   * lands it still names the assets that just went. Confirming inside that
   * window re-sent them, the backend reported them as "not found or still
   * uploading", and a delete that fully succeeded ended in a red "Deleted 1,
   * skipped 1". Pinning the ids here makes the second pass independent of
   * refetch timing rather than merely usually ahead of it.
   */
  const [cascade, setCascade] = useState<{
    ids: string[];
    assets: CascadeAsset[];
    names: Record<string, string>;
  } | null>(null);

  // Publishing requires a nameplate, KV lineage, and unexpired rights. The rule
  // and the call live in the hook, shared with the bulk-edit overlay's copy of
  // this button so the two cannot disagree about who is eligible.
  //
  // On a partial publish the selection narrows to the rows the backend refused:
  // the successful ones have left this tab anyway, and leaving them selected
  // would put a stale count next to a toast explaining that some failed.
  const publish = useAssetPublish(selectedAssets, {
    onPartiallyPublished: selection.replace,
  });

  // Only what can actually be downloaded; the rest of a mixed selection is
  // left out rather than sent for the backend to refuse, and the label says
  // how many that is. One image downloads as itself, no zip.
  const downloadable = selectedAssets.filter(isDownloadable);
  const fileDownload = useFileDownload();
  const downloadStatus = fileDownload.statusOf('selection');
  const tooManyToDownload = downloadable.length > MAX_DOWNLOAD_SELECTION;
  const downloadDisabled =
    isBriefingRow || downloadable.length === 0 || tooManyToDownload || downloadStatus.isDownloading;
  const downloadHint = isBriefingRow
    ? 'Unselect the media-plan row to download'
    : downloadable.length === 0
      ? 'Only published source images with rights in date can be downloaded'
      : tooManyToDownload
        ? `Select at most ${MAX_DOWNLOAD_SELECTION} source images to download at once`
        : downloadable.length < count
          ? `${count - downloadable.length} of ${count} left out: not a published source image, or rights expired`
          : undefined;
  const handleDownload = () => {
    const ids = downloadable.map((asset) => asset.id);
    void fileDownload.download('selection', (onProgress) =>
      ids.length === 1 ? downloadAsset(ids[0], onProgress) : downloadSelection(ids, onProgress),
    );
  };

  const remove = useMutation({
    // Ids are an argument rather than read from `selectedAssets` at call time,
    // so the cascade retry can name exactly the rows that were skipped. See
    // `cascade`.
    //
    // `names` rides along for the same reason: the toast names what it could
    // not delete, and by the time the answer lands the rows may have left
    // `selectedAssets`.
    mutationFn: ({
      ids,
      confirmCascade,
    }: {
      ids: string[];
      confirmCascade: boolean;
      names: Record<string, string>;
    }) =>
      deleteAssets({
        asset_ids: ids,
        ...(confirmCascade ? { confirm_cascade: true } : {}),
      }),
    onSuccess: (result, { confirmCascade, names }) => {
      // Only the cascade guard is retryable, and only on the first pass: the
      // other skips — a published derivative, say — are refusals that a flag
      // does not change, and re-asking on the second pass would loop.
      //
      // Matched on `code`, never on the reason text: the sentence is prose and
      // gets reworded, and a match that silently stops matching would leave the
      // user with a red toast telling them to confirm and nothing to confirm
      // with — making source images undeletable from the UI.
      const blocked = confirmCascade
        ? []
        : result.skipped.filter((skip) => skip.code === SKIP_CASCADE_UNCONFIRMED);
      if (blocked.length > 0) {
        // Deliberately not closing the first modal yet: the assets are still
        // selected and still undeleted, so returning to the grid would suggest
        // the action had finished.
        setCascade({
          ids: blocked.map((skip) => skip.asset_id),
          // One entry per asset: a derivative cut from two selected source
          // images is in both skips' lists, and would otherwise be counted
          // and listed twice.
          assets: [
            ...new Map(
              blocked.flatMap((skip) => skip.cascade ?? []).map((a) => [a.asset_id, a]),
            ).values(),
          ],
          names,
        });
        if (result.updated.length > 0) {
          toast.success(
            `Deleted ${result.updated.length} asset${result.updated.length === 1 ? '' : 's'}`,
          );
          void queryClient.invalidateQueries({ queryKey: ['asset-library', 'assets'] });
          void queryClient.invalidateQueries({ queryKey: ASSET_URLS_QUERY_KEY });
        }
        return;
      }

      if (result.skipped.length > 0) {
        toast.error(describeDeleteSkips(result.updated.length, result.skipped, names), {
          style: { whiteSpace: 'pre-line' },
        });
      } else {
        toast.success(
          `Deleted ${result.updated.length} asset${result.updated.length === 1 ? '' : 's'}`,
        );
      }
      setIsConfirmOpen(false);
      setCascade(null);
      void queryClient.invalidateQueries({ queryKey: ['asset-library', 'assets'] });
      // The URL cache is keyed by asset id and would otherwise keep handing out
      // signed URLs for objects that no longer exist.
      void queryClient.invalidateQueries({ queryKey: ASSET_URLS_QUERY_KEY });
    },
    onError: () => {
      // The modal stays open on failure so the action can be retried without
      // re-selecting; closing it would read as though the delete had happened.
      toast.error('Could not delete');
    },
  });

  return (
    <div className="flex items-center gap-3 pb-2.5 text-[12px]">
      {count > 0 ? (
        <>
          <span className="font-medium text-slate-700">
            {count} selected
            {/* Scoped explicitly: select-all covers what's loaded and filtered,
                not every asset in the library, and a bulk edit that quietly
                reached further than the screen would be a nasty surprise. */}
            {allSelected && totalVisible > 0 && ' (all on this page)'}
          </span>
          {!allSelected && (
            <button type="button" onClick={selectAll} className="text-[#0014cc] hover:underline">
              Select all
            </button>
          )}
          <button type="button" onClick={clear} className="text-slate-500 hover:underline">
            Clear
          </button>

          {/* No count in the label — "N selected" is two elements to the left,
              and the Publish button already learned not to repeat it. */}
          {count === 1 || !canEdit ? (
            <button type="button" onClick={onEdit} className="text-[#0014cc] hover:underline">
              Open
            </button>
          ) : (
            // Bulk edit is a library action, so with a row picked it waits like
            // Download and Delete. Open stays: looking at an asset is not one.
            <HintTooltip hint={isBriefingRow ? 'Unselect the media-plan row to edit' : undefined}>
              <button
                type="button"
                disabled={isBriefingRow}
                onClick={onEdit}
                className="text-[#0014cc] hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline"
              >
                Edit
              </button>
            </HintTooltip>
          )}

          {canDownload && (
            <HintTooltip hint={downloadHint}>
              <button
                type="button"
                disabled={downloadDisabled}
                aria-busy={downloadStatus.isDownloading}
                onClick={handleDownload}
                className="inline-flex items-center gap-1 text-[#0014cc] hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline"
              >
                {/* A spinner, never a percentage: the zip streams with no
                  Content-Length, so there is no total to count towards. */}
                {downloadStatus.isDownloading && <Loader2 className="h-3 w-3 animate-spin" />}
                {downloadStatus.isDownloading
                  ? 'Downloading…'
                  : !isBriefingRow && downloadable.length > 0 && downloadable.length < count
                    ? `Download ${downloadable.length} of ${count}`
                    : 'Download'}
              </button>
            </HintTooltip>
          )}

          {canDelete ? (
            // `ml-auto` on a wrapper, not the button: with a row picked the
            // button sits inside the hint's own span.
            <span className="ml-auto inline-flex">
              <HintTooltip
                hint={isBriefingRow ? 'Unselect the media-plan row to delete' : undefined}
              >
                <button
                  type="button"
                  disabled={isBriefingRow || remove.isPending}
                  onClick={() => setIsConfirmOpen(true)}
                  // Red text, not a red fill: destructive, but not the action this
                  // bar is primarily for, and a second filled button would compete
                  // with Publish. `ml-auto` sits here rather than on Publish because
                  // Delete is the one button present on both tabs — on Published,
                  // Publish is gone and this still has to hold the right edge.
                  className="rounded-lg px-3 py-1.5 text-[12px] font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:text-slate-400"
                >
                  {remove.isPending ? 'Deleting…' : count === 1 ? 'Delete' : 'Delete all'}
                </button>
              </HintTooltip>
            </span>
          ) : (
            // Holds the right edge in Delete's place, so the remaining
            // buttons sit where they always do for roles that cannot delete.
            <span className="ml-auto" />
          )}

          {/* Filling a basket one card at a time is fine for three and painful
              for thirty, and the selection model is already here. No count in
              the label — "N selected" is at the other end of the same strip. */}
          {onAddSelectionToBasket && (
            <button
              type="button"
              disabled={addableCount === 0}
              onClick={onAddSelectionToBasket}
              title={
                addableCount === 0
                  ? 'Nothing new to add: the selection is either ineligible for this row or already in the basket'
                  : addableCount < count
                    ? `${count - addableCount} of ${count} skipped: ineligible or already added`
                    : undefined
              }
              className="rounded-lg bg-[#0014cc] px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-[#0010a3] disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
            >
              Add to basket
            </button>
          )}

          {canPublish && (
            <button
              type="button"
              disabled={publish.publishable === 0 || publish.isPending}
              onClick={() => publish.publish()}
              title={publish.title}
              className="rounded-lg bg-[#0014cc] px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-[#0010a3] disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
            >
              {publish.isPending ? 'Publishing…' : count === 1 ? 'Publish' : 'Publish all'}
            </button>
          )}
        </>
      ) : needsRowToSelect && !hasRowSelected ? (
        <span className="text-slate-400">Select a media-plan row to start</span>
      ) : showBriefingHints && hasRowSelected && rowMissingTier ? (
        <span role="alert" className="font-medium text-red-600">
          This media-plan row has no tier, so no asset can match it. Add the tier in the media plan.
        </span>
      ) : showBriefingHints && hasRowSelected && eligibleCount === 0 ? (
        <span className="font-medium text-amber-600">
          {hasExpiredMatches
            ? 'Matching assets exist for this row, but their rights have expired'
            : 'No published assets match the selected row'}
        </span>
      ) : canSelect ? (
        <span className="text-slate-400">
          Click to select · double-click to open · shift-click for a range · ⌘/ctrl-click to add
        </span>
      ) : (
        <span className="text-slate-400">Click an asset to open it</span>
      )}

      {onToggleGrouping && (
        <div className={count === 0 ? 'ml-auto' : ''}>
          <button
            type="button"
            onClick={onToggleGrouping}
            aria-pressed={isGrouped}
            title={isGrouped ? 'Switch to flat grid' : 'Group assets by Nameplate and Type'}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[12px] font-medium transition-colors ${
              isGrouped
                ? 'border-[#0014cc] bg-blue-50/70 text-[#0014cc] hover:bg-blue-100/70'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Layers className="h-3.5 w-3.5" />
            <span>Group assets</span>
          </button>
        </div>
      )}

      <ConfirmModal
        isOpen={isConfirmOpen && cascade === null}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={() =>
          remove.mutate({
            ids: selectedAssets.map((asset) => asset.id),
            confirmCascade: false,
            names: Object.fromEntries(
              selectedAssets.map((asset) => [asset.id, asset.filename_original]),
            ),
          })
        }
        isLoading={remove.isPending}
        variant="danger"
        title={`Delete ${count} asset${count === 1 ? '' : 's'}?`}
        message="The files are removed from storage as well as the library. This cannot be undone."
        confirmText="Delete"
      />

      {/* The second ask, and only when the backend says there is one. Its
          message is the server's own reason string: it counts derivatives that
          may not be on this page, so anything the grid composed itself would
          risk naming a smaller number than the delete would actually take. */}
      <ConfirmModal
        isOpen={cascade !== null}
        onClose={() => {
          setCascade(null);
          setIsConfirmOpen(false);
        }}
        // Only the skipped ids. The rest of the selection was deleted by the
        // first pass, and re-sending it races the refetch that clears it.
        onConfirm={() =>
          remove.mutate({
            ids: cascade?.ids ?? [],
            confirmCascade: true,
            names: cascade?.names ?? {},
          })
        }
        isLoading={remove.isPending}
        variant="danger"
        title="Also delete the assets made from it?"
        message={
          <>
            <p>{describeCascade(cascade?.assets ?? [], cascade?.ids.length ?? 0)}</p>
            {/* Named as well as counted: "3 assets" is easy to confirm without
                realising one of them is the hero of a live campaign. */}
            {(cascade?.assets.length ?? 0) > 0 && (
              <ul className="mt-3 max-h-48 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200 text-left text-[12px]">
                {cascade?.assets.map((asset) => (
                  <li key={asset.asset_id} className="flex items-center gap-2 px-3 py-1.5">
                    <span className="min-w-0 flex-1 truncate text-slate-700" title={asset.filename}>
                      {asset.filename}
                    </span>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        cascadeBadge(asset.status).className
                      }`}
                    >
                      {cascadeBadge(asset.status).label}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        }
        confirmText="Delete everything"
      />
    </div>
  );
}
